/**
 * `docs/DESIGN.md`: "Screen Wake Lock during a match, re-acquired on
 * `visibilitychange` since it is released when the page hides."
 *
 * The browser releases a wake lock automatically whenever the tab is
 * backgrounded — that is standard Wake Lock API behaviour, not a bug — so
 * `handleVisibilityChange` is the whole re-acquisition story: call it from
 * the same `visibilitychange` listener the game loop already uses to pause,
 * and it puts the lock back only if a match was asking for one.
 */

interface NavigatorWithWakeLock extends Navigator {
  readonly wakeLock: {
    request(type: 'screen'): Promise<WakeLockSentinel>;
  };
}

function hasWakeLock(navigatorRef: Navigator): navigatorRef is NavigatorWithWakeLock {
  return 'wakeLock' in navigatorRef;
}

export class WakeLockController {
  private sentinel: WakeLockSentinel | null = null;
  /** True while a match wants the lock held, independent of whether it is currently acquired. */
  private wanted = false;
  /** The request in flight, if any — so two `acquire()` calls share one sentinel instead of each asking for their own. */
  private pending: Promise<void> | null = null;

  constructor(private readonly navigatorRef: Navigator = navigator) {}

  get isSupported(): boolean {
    return hasWakeLock(this.navigatorRef);
  }

  get isActive(): boolean {
    return this.sentinel !== null;
  }

  /** Ask for the lock. Safe to call when unsupported — it just does nothing. */
  async acquire(): Promise<void> {
    this.wanted = true;
    if (!hasWakeLock(this.navigatorRef) || this.sentinel !== null) return;
    if (this.pending !== null) {
      await this.pending;
      return;
    }

    this.pending = this.request(this.navigatorRef);
    try {
      await this.pending;
    } finally {
      this.pending = null;
    }
  }

  private async request(navigatorRef: NavigatorWithWakeLock): Promise<void> {
    try {
      const sentinel = await navigatorRef.wakeLock.request('screen');
      // The intent may have changed while the request was in flight: a match
      // that ended (or a screen that unmounted) called `release()`, which
      // found no sentinel to let go of because this one had not arrived yet.
      // Keeping it now would hold the screen awake with nothing left that
      // will ever release it — so give it straight back.
      if (!this.wanted) {
        try {
          await sentinel.release();
        } catch {
          // Already released.
        }
        return;
      }
      sentinel.addEventListener('release', () => {
        if (this.sentinel === sentinel) this.sentinel = null;
      });
      this.sentinel = sentinel;
    } catch {
      // Denied, or the page was already hidden by the time the promise
      // resolved — `handleVisibilityChange` will try again.
      this.sentinel = null;
    }
  }

  /** Give the lock up. Call when a match ends. */
  async release(): Promise<void> {
    this.wanted = false;
    const current = this.sentinel;
    this.sentinel = null;
    if (current !== null) {
      try {
        await current.release();
      } catch {
        // Already released.
      }
    }
  }

  /** Re-acquire when the page becomes visible again, only if a match still wants the lock. */
  handleVisibilityChange(
    visibilityState: DocumentVisibilityState = document.visibilityState,
  ): void {
    if (this.wanted && visibilityState === 'visible' && this.sentinel === null) {
      void this.acquire();
    }
  }
}
